import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ClockCounterClockwise, Files, Printer, ChatCircleText, FileDashed, Books, Trash, HardDrives, UserFocus, Folders, EyeSlash, ShareNetwork } from '@phosphor-icons/react';
import { ModuleSidebar } from '@/components/enterprise/ModuleSidebar';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { dmsApi } from './dmsApi';
import { nasApi } from './nas/nasApi';

// UX-2.5A · DOCUMENTS MODEL A — one module sidebar owns Documents navigation.
// The folder tree is content navigation and lives inside すべての書類, never as
// a second permanent aside. The module name appears only in the header chip.
// 並びは「やりたいこと」順（検索・閲覧 → 取り込み → 整理・管理）。項目名は名詞で
// 統一し、一目で何の画面か分かる言葉にする（「スマート版管理」では伝わらない）。
const viewOf = (loc) => new URLSearchParams(loc.search).get('view') || 'home';
const onView = (v) => (loc) => loc.pathname === '/documents' && viewOf(loc) === v;

// 非表示にした画面は、URLを直接開いても中身を出さない（APIは403だが、
// 空の一覧に見えると「使えるのに空」と誤解される）。
const VIEW_KEY = { home: 'recent', browse: 'all', scan: 'scan',
                   bundles: 'bundles', requests: 'requests', trash: 'trash', shares: 'shares' };
const PATH_KEY = [
  ['/documents/assistant', 'ask'],
  ['/documents/responsibilities', 'responsibilities'],
  ['/documents/drawing-revisions', 'drawing_revs'],
  ['/documents/shares', 'shares'],
  ['/documents/nas', 'nas'],
];
const navKeyOf = (loc) => {
  const hit = PATH_KEY.find(([p]) => loc.pathname.startsWith(p));
  if (hit) return hit[1];
  return loc.pathname === '/documents' ? VIEW_KEY[viewOf(loc)] || null : null;
};

export default function DocsLayout() {
  const { user } = useAuth();
  const { t } = useT();
  const location = useLocation();
  const [features, setFeatures] = useState({});
  const [hidden, setHidden] = useState([]);
  const [nasVisible, setNasVisible] = useState(false);

  useEffect(() => {
    dmsApi.meta().then((m) => {
      setFeatures(m?.features || {});
      setHidden(m?.nav_hidden || []);
    }).catch(() => {});
  }, []);

  // NAS は運用中のファイルサーバー。この画面は AI 検索の参照範囲を見るためのもので、
  // 独自フラグ（nas_archive_page）で制御する。すべての書類の navigation には影響しない。
  useEffect(() => {
    if (!isAdmin(user)) { setNasVisible(false); return; }
    nasApi.meta().then((m) => setNasVisible(!!m?.flags?.nas_archive_page || !!m?.can_manage))
      .catch(() => setNasVisible(false));
  }, [user]);

  const on = (key) => !hidden.includes(key);

  const sections = [
    { label: t('docs.navGroup.find'), items: [
      ...(on('recent') ? [
        { to: '/documents?view=home', label: t('docs.nav.recent'), icon: ClockCounterClockwise,
          testid: 'docs-nav-recent', activeWhen: onView('home') }] : []),
      { to: '/documents?view=browse', label: t('docs.nav.all'), icon: Files,
        testid: 'docs-nav-all', activeWhen: onView('browse') },
      ...(on('ask') ? [
        { to: '/documents/assistant', label: t('docs.nav.ask'), icon: ChatCircleText,
          testid: 'docs-nav-assistant' }] : []),
      ...(nasVisible && on('nas') ? [
        { to: '/documents/nas', label: t('docs.nav.nas'), icon: HardDrives,
          testid: 'docs-nav-nas' }] : []),
    ] },
    { label: t('docs.navGroup.intake'), items: [
      ...(features.scanner_inbox && on('scan') ? [
        { to: '/documents?view=scan', label: t('docs.nav.scan'), icon: Printer,
          testid: 'docs-nav-scan', activeWhen: onView('scan') }] : []),
    ] },
    { label: t('docs.navGroup.organise'), items: [
      ...(on('drawing_revs') ? [
        { to: '/documents/drawing-revisions', label: t('docs.nav.drawingRevs'), icon: Folders,
          testid: 'docs-nav-drawing-revs' }] : []),
      ...(features.bundles_binders_envelopes && on('bundles') ? [
        { to: '/documents?view=bundles', label: t('docs.nav.bundles'), icon: Books,
          testid: 'docs-nav-bundles', activeWhen: onView('bundles') }] : []),
      ...(on('responsibilities') ? [
        { to: '/documents/responsibilities', label: t('docs.nav.responsibilities'), icon: UserFocus,
          testid: 'docs-nav-responsibilities' }] : []),
      ...(on('shares') ? [
        { to: '/documents/shares', label: t('docs.nav.shares'), icon: ShareNetwork,
          testid: 'docs-nav-shares' }] : []),
      ...(features.document_requests && on('requests') ? [
        { to: '/documents?view=requests', label: t('docs.nav.requests'), icon: FileDashed,
          testid: 'docs-nav-requests', activeWhen: onView('requests') }] : []),
    ] },
    { label: t('docs.navGroup.other'), items: [
      ...(on('trash') ? [
        { to: '/documents?view=trash', label: t('docs.nav.trash'), icon: Trash,
          testid: 'docs-nav-trash', activeWhen: onView('trash') }] : []),
    ] },
  ].filter((s) => s.items.length > 0);

  const currentKey = navKeyOf(location);
  const blocked = !!currentKey && hidden.includes(currentKey);

  return (
    <div className="flex flex-col md:flex-row gap-5 md:gap-6" data-testid="docs-layout">
      <ModuleSidebar title="ドキュメント" sections={sections} testid="docs-sidebar" />
      <div className="flex-1 min-w-0">
        {blocked ? (
          <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center"
            data-testid="docs-nav-blocked">
            <EyeSlash size={34} weight="duotone" className="mx-auto mb-2 text-zinc-400" />
            <p className="text-sm font-semibold text-zinc-700">この画面は管理者により非表示に設定されています</p>
            <p className="mt-1 text-xs text-zinc-500">
              必要な場合は管理者にご連絡ください（ドキュメント設定 →「サイドバーの表示」）
            </p>
          </div>
        ) : <Outlet />}
      </div>
    </div>
  );
}
